import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { CircularProgress, Collapse, ListItemText } from '@mui/material';
import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';
import { Control, Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import usePagebuilder from '../../../../../../hooks/pagebuilder/usePagebuilder';
import { AgendaEntry, SpeakerEntry } from '../../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../../reduxToolkit/typedRedux';
import { getSpeakersForEvent, updateAgendaEntry } from '../../../../../../services/services';
import validationBuilder from '../../../../../../static/validations';
import { CustomTranslationForm } from '../../../../../common/CustomTranslationForm';
import VyColorPickerV2 from '../../../../../common/VyColorPickerV2';
import VyDateTimePicker from '../../../../../common/VyDateTimePicker';
import VyTextField from '../../../../../common/VyTextField';
import VyUpload from '../../../../../common/VyUpload';
import InlineEditButton from '../../../../pageBuilder/InlineEditButton';
import { SpeakerSelect } from '../../../../pageBuilder/Settings/agenda/SpeakerSelect';

const VyRichTextEditor = dynamic(() => import('../../../../../common/VyRichTextEditor'), {
  ssr: false,
  loading: () => <p>loading...</p>,
});

interface EntryInlineEditProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  entry: AgendaEntry;
  uiComponent: Partial<any>;
  setIsEditMode: (value: boolean) => void;
}

const EntryInlineEdit = ({ entry, uiComponent, setIsEditMode }: EntryInlineEditProps) => {
  const event = useSelector((state) => state.common.event);

  const [availableSpeakers, setAvailableSpeakers] = useState<SpeakerEntry[]>();
  const [showMore, setShowMore] = useState(false);

  const { t } = useTranslation();
  const methods = useForm<AgendaEntry>({ mode: 'all' });
  const { editComponent } = usePagebuilder(true);

  const watchSpeakers: any = methods.watch('speakers', []);

  async function submit(props: AgendaEntry) {
    const newComp = { ...uiComponent };

    if (!entry?._id || !newComp?.entryList) return;

    try {
      newComp.entryList = [...newComp.entryList];
      const entryIndex = entry ? newComp.entryList.indexOf(entry) : -1;

      const updatedEntry = { ...props, speakers: watchSpeakers ?? [] };

      newComp.entryList[entryIndex] = { ...newComp.entryList[entryIndex], ...updatedEntry };
      await updateAgendaEntry(entry?._id, updatedEntry);

      editComponent({ ...uiComponent, entryList: newComp.entryList });
      setIsEditMode(false);
    } catch (err) {
      console.error(err);
    }
  }

  const handleChange = () => {
    setShowMore((prev) => !prev);
  };

  async function fetchSpeakers() {
    if (!event) return;
    const speakers = await getSpeakersForEvent(event._id);
    setAvailableSpeakers(speakers);
  }

  useEffect(() => {
    fetchSpeakers();
  }, [event]);

  useEffect(() => {
    methods.reset(entry);
  }, [availableSpeakers]);

  const validation = validationBuilder(t);

  if (!availableSpeakers) return <CircularProgress />;

  return (
    <FormProvider {...methods}>
      <form id={entry._id} onSubmit={methods.handleSubmit(submit)} className="relative flex w-full space-y-2">
        <div className={`flex items-center`}>
          <div className={`flex flex-col m-1 @md:flex-row @md:m-4 cursor-default`}>
            <Controller
              control={methods.control}
              name="timestamp"
              defaultValue={entry?.timestamp ?? new Date(event!.datetime_start) ?? undefined}
              render={({ field: { onChange, value } }) => (
                <VyDateTimePicker
                  customStyle="flex flex-col space-y-2 w-40"
                  defaultDate={new Date(value)}
                  onChange={(value) => onChange(value?.toISOString())}
                />
              )}
            />
            <div className="@md:ml-4">
              <div className="flex">
                <div className="flex flex-col">
                  <Controller
                    control={methods.control}
                    name={`title`}
                    rules={{
                      minLength: { value: 3, message: t(`pagebuilder:entry-error-title-length`) },
                      required: t(`pagebuilder:entry-error-title-required`) as string,
                    }}
                    defaultValue={entry.title}
                    render={({ field }) => <VyTextField {...field} label={t(`pagebuilder:entry-title`)} fullWidth />}
                  />
                  <CustomTranslationForm control={(methods.control as unknown) as Control} watch={methods.watch} fieldName="titleTranslations" />
                </div>
                <Controller
                  control={methods.control}
                  name="titleColor"
                  defaultValue={entry?.titleColor}
                  render={({ field: { onChange, value } }) => <VyColorPickerV2 defaultValue={value} onChange={(color) => onChange(color)} />}
                />
              </div>
              <div className="flex">
                <div className="flex flex-col">
                  <Controller
                    control={methods.control}
                    name={`description`}
                    render={({ field }) => <VyTextField {...field} defaultValue={entry.description} label={t(`pagebuilder:entry-description`)} fullWidth />}
                  />
                  <CustomTranslationForm
                    defaultValue={entry.descriptionTranslations}
                    control={(methods.control as unknown) as Control}
                    watch={methods.watch}
                    fieldName="descriptionTranslations"
                  />
                </div>
                <Controller
                  control={methods.control}
                  name="descriptionColor"
                  defaultValue={entry?.descriptionColor}
                  render={({ field: { onChange, value } }) => <VyColorPickerV2 defaultValue={value} onChange={(color) => onChange(color)} />}
                />
              </div>
              <SpeakerSelect availableSpeakers={availableSpeakers} />
              <Controller
                control={methods.control}
                name={`url`}
                rules={validation('urlWithoutProtocol')}
                render={({ field }) => <VyTextField {...field} className="mt-2" defaultValue={entry.url} label="URL" fullWidth />}
              />
              {methods.formState.errors?.url?.message && <small>{methods.formState.errors?.url?.message}</small>}
              <div className="flex items-center justify-between w-full p-2 rounded cursor-pointer" onClick={handleChange}>
                <ListItemText primary={t('pagebuilder:open-details')} />
                {showMore ? <ExpandLess /> : <ExpandMore />}
              </div>
              <Collapse in={showMore} timeout="auto">
                <div>
                  <span className="mt-2">{t(`pagebuilder:entry-image`)}</span>
                  <Controller
                    control={methods.control}
                    name="image"
                    defaultValue={entry?.image}
                    render={({ field: { onChange, value } }) => (
                      <VyUpload
                        defaultFileId={value}
                        showFileUploadProgress={true}
                        acceptedFileTypes="image/*"
                        randomExternalImageButton={false}
                        onChange={(f) => {
                          onChange(f);
                        }}
                        showPreview={true}
                      />
                    )}
                  />
                  <span className="mt-6 text-xl">{t(`pagebuilder:entry-additional-text`)}</span>
                  <Controller
                    control={methods.control}
                    name="additionalText"
                    defaultValue={entry?.additionalText}
                    render={({ field: { onChange, value } }) => <VyRichTextEditor defaultValue={value ?? ''} onBlur={(text) => onChange(text)} />}
                  />
                </div>
              </Collapse>
            </div>
            <div className="absolute flex flex-col top-2 right-14">
              <button className="p-2 mb-1 text-white bg-black" type="submit">
                {t('common:save')}
              </button>
            </div>
            <InlineEditButton
              isEditMode={true}
              setIsEditMode={() => {
                setIsEditMode(false);
              }}
              tooltip={t('common:cancel')}
              forceShow
            />
          </div>
          <style jsx>
            {`
              .speaker {
                transition: color 0.15s cubic-bezier(0.2, 0, 0.2, 1);
                cursor: pointer;
              }
            `}
          </style>
        </div>
      </form>
    </FormProvider>
  );
};

export default EntryInlineEdit;
